import { useEffect, useMemo, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useNavigate } from 'react-router-dom';

interface ReportItem {
  id: number;
  report_uid: string;
  victim_uid: string;
  incident_type: string;
  urgency: string;
  description: string;
  location: string;
  child_info: string;
  is_anonymous: string;
  reporter_name: string;
  reporter_contact: string;
  photo_evidence: string;
  status: string;
  created_at: string;
  victim_name?: string;
}

export default function SWRaiseConcernPage() {

  const [reports, setReports] = useState<ReportItem[]>([]);
  const [selectedReport, setSelectedReport] = useState<ReportItem | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const fetchReports = async () => {

    try {

      const res = await fetch(
        'https://guardiancloudcare.com/backend/api/get_victim_reports.php'
      );

      const data = await res.json();

      if (Array.isArray(data)) {

        setReports(data);

        if (data.length > 0) {
          setSelectedReport(data[0]);
        }

      }

    } catch (err) {

      console.error(err);

    } finally {

      setLoading(false);

    }

  };

  useEffect(() => {
    fetchReports();
  }, []);

  const filteredReports = useMemo(() => {

    return reports.filter((report) => {

      const keyword = search.toLowerCase();

      const matchSearch =
        report.report_uid?.toLowerCase().includes(keyword) ||
        report.incident_type?.toLowerCase().includes(keyword) ||
        report.location?.toLowerCase().includes(keyword) ||
        report.reporter_name?.toLowerCase().includes(keyword);

      const matchStatus =
        !statusFilter ||
        report.status?.toLowerCase() === statusFilter.toLowerCase();

      const matchType =
        !typeFilter ||
        report.incident_type?.toLowerCase() === typeFilter.toLowerCase();

      return matchSearch && matchStatus && matchType;

    });

  }, [reports, search, statusFilter, typeFilter]);

  const totalReports = reports.length;

  const newReports =
    reports.filter(r => r.status === 'pending').length;

  const inProgress =
    reports.filter(r => r.status === 'in_progress').length;

  const resolved =
    reports.filter(r => r.status === 'resolved').length;

  const updateStatus = async (
    report_uid: string,
    status: string
  ) => {

    try {

      const res = await fetch(
        'https://guardiancloudcare.com/backend/api/update_victim_report_status.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            report_uid,
            status,
          }),
        }
      );

      const data = await res.json();

      if (data.success) {
        fetchReports();
      }

    } catch (err) {

      console.error(err);

    }

  };

  const getStatusStyle = (status: string) => {

    switch ((status || '').toLowerCase()) {

      case 'pending':
        return 'bg-blue-100 text-blue-700';

      case 'in_review':
        return 'bg-yellow-100 text-yellow-700';

      case 'in_progress':
        return 'bg-purple-100 text-purple-700';

      case 'resolved':
        return 'bg-green-100 text-green-700';

      default:
        return 'bg-gray-100 text-gray-600';

    }

  };

  const parsePhotos = (photos: string) => {

    try {

      return JSON.parse(photos || '[]');

    } catch {

      return [];

    }

  };

  return (
    <AppLayout
      title="Raise Concern"
      breadcrumb={['Social Worker', 'Raise Concern']}
    >
      <div className="space-y-5">

        {/* HEADER */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">

          <div>
            <h1 className="text-3xl font-bold text-[#1f2a5a]">
              Raise Concern
            </h1>

            <p className="text-gray-500 mt-1">
              View and manage all concerns raised by the public.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 w-full xl:w-auto">

            <TopStat
              icon="ri-file-list-3-line"
              title="Total Reports"
              value={String(totalReports)}
              desc="All time"
              color="blue"
            />

            <TopStat
              icon="ri-time-line"
              title="New Reports"
              value={String(newReports)}
              desc="Needs review"
              color="yellow"
            />

            <TopStat
              icon="ri-loader-4-line"
              title="In Progress"
              value={String(inProgress)}
              desc="Ongoing cases"
              color="purple"
            />

            <TopStat
              icon="ri-checkbox-circle-line"
              title="Resolved"
              value={String(resolved)}
              desc="Completed cases"
              color="green"
            />

          </div>
        </div>

        {/* MAIN */}
        <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">

          {/* TABLE */}
          <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">

            {/* FILTER */}
            <div className="p-5 border-b border-gray-100">

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">

                <div className="relative md:col-span-2">
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by ID, type, location, or name..."
                    className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 outline-none"
                  />
                </div>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-3 rounded-2xl border border-gray-200 outline-none"
                >
                  <option value="">All Status</option>
                  <option value="pending">New</option>
                  <option value="in_review">In Review</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </select>

                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-4 py-3 rounded-2xl border border-gray-200 outline-none"
                >
                  <option value="">All Types</option>

                  {[...new Set(
                    reports.map(r => r.incident_type)
                  )].map((type) => (
                    <option key={type}>
                      {type}
                    </option>
                  ))}
                </select>

              </div>

            </div>

            {/* TABLE */}
            <div className="overflow-x-auto">

              <table className="w-full min-w-[1000px]">

                <thead className="bg-gray-50 border-b border-gray-100">

                  <tr className="text-left text-sm text-gray-500">

                    <th className="px-5 py-4">ID</th>
                    <th className="px-5 py-4">Type of Concern</th>
                    <th className="px-5 py-4">Reported By</th>
                    <th className="px-5 py-4">Location</th>
                    <th className="px-5 py-4">Date Reported</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4">Action</th>

                  </tr>

                </thead>

                <tbody>

                  {filteredReports.map((report) => (

                    <tr
                      key={report.id}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >

                      <td className="px-5 py-4 text-sm font-semibold text-[#5f6ea5]">
                        {report.report_uid}
                      </td>

                      <td className="px-5 py-4">

                        <div className="flex items-center gap-3">

                          <div className="w-11 h-11 rounded-2xl bg-red-50 flex items-center justify-center">
                            <i className="ri-alarm-warning-line text-red-500 text-lg" />
                          </div>

                          <div>

                            <p className="font-semibold text-gray-800">
                              {report.incident_type}
                            </p>

                            <p className="text-xs text-gray-400">
                              Child
                            </p>

                          </div>

                        </div>

                      </td>

                      <td className="px-5 py-4">

                        <div>

                          <p className="font-medium text-gray-800">
                            {report.is_anonymous === '1'
                              ? 'Anonymous'
                              : report.reporter_name}
                          </p>

                          <p className="text-xs text-gray-400">
                            {report.reporter_contact}
                          </p>

                        </div>

                      </td>

                      <td className="px-5 py-4 text-sm text-gray-600">
                        {report.location}
                      </td>

                      <td className="px-5 py-4 text-sm text-gray-500">
                        {report.created_at}
                      </td>

                      <td className="px-5 py-4">

                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(report.status)}`}>
                          {report.status}
                        </span>

                      </td>

                      <td className="px-5 py-4">

                        <button
                        onClick={() => navigate(`/sw-raise-concern/${report.report_uid}`)}
                        className="w-10 h-10 rounded-xl border border-gray-200 hover:bg-gray-50"
                        >
                        <i className="ri-eye-line" />
                        </button>

                      </td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </div>

          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function TopStat({
  icon,
  title,
  value,
  desc,
  color,
}: any) {

  const colors: any = {
    blue: 'bg-blue-100 text-blue-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-100 text-purple-600',
    green: 'bg-green-100 text-green-600',
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${colors[color]}`}>
        <i className={`${icon} text-2xl`} />
      </div>

      <div className="mt-4">
        <p className="text-sm text-gray-500">{title}</p>

        <h2 className="text-3xl font-bold text-gray-900 mt-1">
          {value}
        </h2>

        <p className="text-sm text-gray-400 mt-1">
          {desc}
        </p>
      </div>
    </div>
  );
}

function DetailItem({
  icon,
  label,
  value,
}: any) {

  return (
    <div className="flex gap-4">

      <div className="w-11 h-11 rounded-2xl bg-gray-100 flex items-center justify-center">
        <i className={`${icon} text-gray-600`} />
      </div>

      <div className="flex-1">
        <p className="text-sm text-gray-400">
          {label}
        </p>

        <p className="font-medium text-gray-800 mt-1 whitespace-pre-line">
          {value || 'No information'}
        </p>
      </div>

    </div>
  );
}